/**
 * Проверяет соответствие значения `value` паттерну матчера `pattern`
 *
 * Синтаксис паттернов:
 * - Любые примитивы
 * - Массивы: `[[...]]`
 * - Любое значение: `[]`
 * - Объекты, значения которых - вышеперечисленные паттерны: {...}
 *
 * @param pattern Паттерн матчера
 * @param value Значение, сравниваемое с `pattern`
 * @returns `true`, если `value` соответствует `pattern`, иначе - `false`
 *
 * @example
 * ```js
 * // Примитивы:
 * matchPattern('a', 'a') // true
 * matchPattern(1, 1) // true
 * matchPattern(null, null) // true
 * matchPattern(void 0, void 0) // true
 *
 * // Массивы:
 * matchPattern([[]], []) // true
 * matchPattern([['a', 1]], ['a', 1]) // true
 * matchPattern([[ [] ]], ['a']) // true
 *
 * // Любое значение:
 * matchPattern([], 'a') // true
 * matchPattern([], null) // true
 * matchPattern([], []) // true
 * matchPattern([], ['a']) // true
 *
 * // Объекты:
 * matchPattern({}, {}) // true
 * matchPattern({a: 0}, {a: 0}) // true
 * matchPattern({a: {b: {c: ['b', 'c']}}}, {a: {b: {c: ['b', 'c']}}}) // true
 * matchPattern({}, {a: 'b'}) // true
 * ```
 */
export const matchPattern = (pattern: unknown, value: unknown): boolean => {
  // Если паттерн - массив
  if (Array.isArray(pattern)) {
    // [] pattern должен соответствовать любому элементу
    if (pattern.length === 0) {
      return true
    }

    // [[...]] pattern должен точно соответствовать массиву элементов
    const arrayExpected = pattern[0]
    if (pattern.length === 1 && Array.isArray(arrayExpected)) {
      // [[...]] pattern должен сравниваться со value, который тоже является массивом
      if (!Array.isArray(value)) {
        return false
      }

      // [[]] pattern соответствует пустому массиву []
      if (!arrayExpected.length) {
        return value.length === 0
      }

      // Длины сравниваемых массивов должны быть равны
      if (arrayExpected.length !== value.length) {
        return false
      }

      // Рекурсивное сравнение элементов массивов
      for (let i = 0; i < arrayExpected.length; i++) {
        if (!matchPattern(arrayExpected[i], value[i])) {
          return false
        }
      }

      return true
    }

    return false
  }

  // Если паттерн - объект
  if (typeof pattern === 'object' && pattern !== null) {
    // Объект pattern должен сравниваться с value, который тоже является объектом
    // Учитываем, что typeof null === 'object'
    if (typeof value !== 'object' || value === null) {
      return false
    }

    // Количество полей должно совпадать
    if (Object.keys(pattern).length !== Object.keys(value).length) {
      return false
    }

    // Рекурсивное сравнение объектов по значениям ключей
    for (const key in pattern) {
      if (!Object.prototype.hasOwnProperty.call(pattern, key) || !Object.prototype.hasOwnProperty.call(value, key)) {
        return false
      }

      // Явное приведения типов после явной проверки наличия ключей в объектах
      // `key in object` и `.hasOwnProperty` не сужают типы
      const patternEntry = pattern[key as keyof typeof pattern] as unknown
      const valueEntry = value[key as keyof typeof value] as unknown

      if (!matchPattern(patternEntry, valueEntry)) {
        return false
      }
    }

    return true
  }

  // Сравнение примитивов
  return pattern === value
}
